import React, { useEffect, useMemo, useState } from 'react';
import {
  TerritoryHeader,
  StatCard,
  RecentTicketsCard,
  EscalatedIssuesCard,
  AutoAlertsCard,
} from './components';
import { CreateTicketModal } from '../shared';
import {
  DashboardStat,
  RecentTicket,
  EscalatedIssue,
  AutoAlert,
} from './callCenterDashboardTypes';
import { useTicketsStore } from '../../../store/ticketsStore';
import { uiPriority, uiStatus, categoryToModule, relativeTime } from '../shared/ticketAdapter';
import { Ticket } from '../../../services/ticketsApi';
import styles from './CallCenterDashboard.module.css';

function adaptRecent(t: Ticket): RecentTicket {
  return {
    id: String(t.id),
    ticketId: `#${t.ticket_number}`,
    module: categoryToModule(t.category),
    customer: t.customer_name || '—',
    priority: uiPriority(t.priority),
    status: uiStatus(t.status),
  };
}

function adaptEscalated(t: Ticket): EscalatedIssue {
  const overdue = t.sla_due_at && new Date(t.sla_due_at).getTime() < Date.now();
  return {
    id: String(t.id),
    ticketId: `#${t.ticket_number}`,
    title: t.subject,
    description: t.description || '—',
    meta: overdue ? `Overdue · SLA breached` : `Opened ${relativeTime(t.opened_at)}`,
    variant: overdue ? 'red' : t.priority === 'critical' ? 'red' : 'yellow',
  };
}

function adaptAlert(t: Ticket): AutoAlert {
  return {
    id: `auto-${t.id}`,
    title: `${categoryToModule(t.category)} alert`,
    description: t.subject,
    timeAgo: relativeTime(t.opened_at),
    variant: t.priority === 'critical' ? 'red' : t.priority === 'high' ? 'yellow' : 'blue',
  };
}

const CallCenterDashboard: React.FC = () => {
  const items = useTicketsStore((s) => s.items);
  const summary = useTicketsStore((s) => s.summary);
  const setFilters = useTicketsStore((s) => s.setFilters);
  const fetchList = useTicketsStore((s) => s.fetchList);
  const fetchSummary = useTicketsStore((s) => s.fetchSummary);

  useEffect(() => {
    setFilters({ category: undefined, limit: 25 });
    fetchList();
    fetchSummary();
  }, [setFilters, fetchList, fetchSummary]);

  const dashboardStats: DashboardStat[] = [
    { id: 'open', label: 'Open Tickets', value: String(summary?.open ?? 0).padStart(2, '0'), color: 'blue', icon: 'open' },
    { id: 'high', label: 'High Priority Issues', value: String(summary?.high_priority ?? 0).padStart(2, '0'), color: 'red', icon: 'highPriority' },
    { id: 'overdue', label: 'Overdue Tickets', value: String(summary?.overdue ?? 0).padStart(2, '0'), color: 'orange', icon: 'overdue' },
    { id: 'resolved', label: 'Resolved Today', value: String(summary?.resolved_today ?? 0).padStart(2, '0'), color: 'green', icon: 'resolved' },
  ];

  const recentTickets = useMemo<RecentTicket[]>(() => items.slice(0, 5).map(adaptRecent), [items]);

  const escalatedIssues = useMemo<EscalatedIssue[]>(
    () => items
      .filter((t) => t.priority === 'critical' || t.priority === 'high')
      .filter((t) => t.status !== 'resolved' && t.status !== 'closed')
      .slice(0, 3)
      .map(adaptEscalated),
    [items]
  );

  const autoAlerts = useMemo<AutoAlert[]>(
    () => items
      .filter((t) => t.source === 'system_alert' || t.source === 'driver_app' || t.source === 'warehouse_staff')
      .slice(0, 3)
      .map(adaptAlert),
    [items]
  );

  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className={styles.container}>
      <TerritoryHeader territory="All Territories" onNewTicket={() => setShowCreate(true)} />

      <div className={styles.statsGrid}>
        {dashboardStats.map((stat) => (
          <StatCard key={stat.id} stat={stat} />
        ))}
      </div>

      <div className={styles.middleRow}>
        <RecentTicketsCard tickets={recentTickets} />
        <EscalatedIssuesCard issues={escalatedIssues} />
      </div>

      <AutoAlertsCard alerts={autoAlerts} />

      <CreateTicketModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => fetchList()}
      />
    </div>
  );
};

export const CallCenterDashboardPage: React.FC = () => <CallCenterDashboard />;

export { CallCenterDashboard };
export default CallCenterDashboard;
